import React, { useEffect, useState } from "react";
import { Empty, Table, Button, Modal, Typography, Row, Col, Image } from "antd";
import {
  LoadingOutlined,
  CalendarOutlined,
  TableOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { get_language, translate } from "../../locale";
import moment from "moment";

import { Link, useParams } from "react-router-dom";
import UserServices from "../../services/UserServices";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import SecondaryButton from "../Buttons/SecondaryButton";
import LogBookServices from "../../services/LogBookServices";

const localizer = momentLocalizer(moment);

const { Title, Text } = Typography;

const PhysicalActivityCalendar = () => {
  const [loading, setLoading] = useState(false);
  const [loadUser, setLoadUser] = useState(false);
  const [data, setData] = useState(null);
  const [date, setDate] = useState(
    moment().startOf("isoWeek").format("YYYY-MM-DD")
  );
  const [patient, setPatient] = useState(null);
  const [dataTable, setDataTable] = useState([]);
  const [modalDetails, setModalDetails] = useState(null);
  const [showAs, setShowAs] = useState("calendar");

  const { username } = useParams();
  const moods = {
    happy: "Contento",
    sad: "Triste",
    neutral: "Neutral",
    angry: "Enojado",
    irritated: "Irritado",
    distressed: "Angustiado",
    anxious: "Ansioso",
    indifferent: "Indiferente",
    excited: "Entusiasmado",
  };

  useEffect(() => {
    loadPatientInfo();
  }, []);

  const loadPatientInfo = async () => {
    try {
      setLoadUser(true);
      const res = await UserServices.getProfile(username);
      if (res.status >= 200 && res.status < 300) {
        setPatient(res.data.my_contact_info);
      }
      setLoadUser(false);
    } catch (error) {
      setLoadUser(false);
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [date]);

  const fetchData = async () => {
    try {
      const response = await LogBookServices.getPhysicalActivity(
        username,
        date
      );
      if (response.status >= 200 && response.status <= 300) {
        let newData = response.data.activity.map((i) => {
          const register_datetime = `${i.activity_date} ${i.activity_time}`;
          return {
            ...i,
            title: new Date(register_datetime),
            start: new Date(register_datetime),
            end: new Date(register_datetime),
            register_datetime: register_datetime,
            type: "cell",
          };
        });

        let caloriesData = {};
        newData.forEach((i) => {
          const date = moment(`${i.register_datetime}`).format("YYYY-MM-DD");
          const tz = `${i.register_datetime}`.substring(19, 25);
          if (!caloriesData[date]) {
            caloriesData = {
              ...caloriesData,
              [date]: {
                calories: i.calories,
                date_time: date + "T00:00:00" + tz,
              },
            };
          } else {
            const calories =
              parseFloat(caloriesData[date].calories) + parseFloat(i.calories);
            caloriesData[date] = { ...caloriesData[date], calories: calories };
          }
        });

        const caloriesLabels = Object.keys(caloriesData).map((i, k) => {
          return {
            id: "dsg_" + k,
            title: caloriesData[i].calories,
            start: new Date(caloriesData[i].date_time),
            end: new Date(caloriesData[i].date_time),
            allDay: true,
            date_time: caloriesData[i].date_time,
            calories: caloriesData[i].calories,
            type: "header",
          };
        });
        setDataTable(response.data.activity);
        // agregamos los labels
        newData = [...newData, ...caloriesLabels];
        setData(newData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 24,
      }}
      spin
    />
  );

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-4 justify-center">
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            className="border-0"
            onClick={() => handleDetails(record)}
          />
        </div>
      ),
    },
    {
      title: "Fecha",
      dataIndex: "activity_date",
      key: "activity_date",
      render: (_, record) =>
        moment(`${record.activity_date} ${record.activity_time}`).format(
          get_language === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY"
        ),
      sorter: (a, b) => {
        if (a.start_datetime < b.start_datetime) return -1;
        if (a.start_datetime > b.start_datetime) return 1;
        return 0;
      },
    },
    {
      title: "Hora",
      dataIndex: "activity_time",
      key: "activity_time",
      render: (_, record) =>
        moment(`${record.activity_date} ${record.activity_time}`).format(
          "HH:mm:ss"
        ),
      sorter: (a, b) => {
        if (a.start_datetime < b.start_datetime) return -1;
        if (a.start_datetime > b.start_datetime) return 1;
        return 0;
      },
    },
    {
      title: "Calorías",
      dataIndex: "calories",
      key: "calories",
      sorter: (a, b) => a.calories - b.calories,
    },
    {
      title: "Estado de ánimo",
      dataIndex: "mood",
      key: "mood",
      render: (mood) => moods[mood],
    },
    {
      title: "Texto",
      dataIndex: "text",
      key: "text",
      render: (text) =>
        text.length > 100 ? `${text.substring(0, 100)}...` : text,
    },
  ];

  const handleHideDetails = () => {
    setModalDetails(null);
  };

  const handlePrev = () => {
    setDate((v) => moment(v).subtract(7, "days").format("YYYY-MM-DD"));
  };
  const handleToday = () => {
    setDate((v) => moment().format("YYYY-MM-DD"));
  };
  const handleNext = () => {
    if (date !== moment().format("YYYY-MM-DD")) {
      setDate((v) => moment(v).add(7, "days").format("YYYY-MM-DD"));
    }
  };
  const handleDetails = (item) => {
    setModalDetails(item);
  };
  return (
    <Row className="w-full">
      <Col flex="column" className="w-full" id="PhysicalActivity">
        <Row className="w-full">
          <Col span={24} className="mt-4">
            <Title level={2} className="text-center">
              {translate("Actividad física de ")}{" "}
              {patient && `${patient.user.full_name}`}
            </Title>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col span={24} className="flex flex-row justify-end space-x-2">
            <span className="flex flex-col justify-center">
              {translate("Formato")}:
            </span>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setShowAs("calendar")}
              type={showAs === "calendar" ? "primary" : "default"}
            />
            <Button
              icon={<TableOutlined />}
              onClick={() => setShowAs("table")}
              type={showAs === "table" ? "primary" : "default"}
            />
          </Col>
        </Row>
        {showAs === "calendar" && (
          <Calendar
            localizer={localizer}
            defaultDate={null}
            defaultView={Views.WEEK}
            views={["week"]}
            events={data || []}
            startAccessor="start"
            endAccessor="end"
            step={60}
            timeslots={6}
            style={{ height: 500 }}
            components={{
              toolbar: (props) => (
                <div className="flex flex-row my-4">
                  <div>
                    <SecondaryButton
                      className="mr-4"
                      onClick={() => {
                        props.onNavigate("PREV");
                        handlePrev();
                      }}
                    >
                      &lt;
                    </SecondaryButton>
                    <SecondaryButton
                      className="mr-4"
                      onClick={() => {
                        props.onNavigate("TODAY");
                        handleToday();
                      }}
                    >
                      Hoy
                    </SecondaryButton>
                    <SecondaryButton
                      className=""
                      onClick={() => {
                        props.onNavigate("NEXT");
                        handleNext();
                      }}
                    >
                      &gt;
                    </SecondaryButton>
                  </div>
                  <div className="flex flex-1 flex-row justify-center items-center">
                    {props.label}
                  </div>
                </div>
              ),
              week: {
                event: ({ event }) => {
                  return (
                    <Button
                      type="text"
                      className={`text-center w-full inline p-0 m-0 h-auto leading-none text-white hover:text-white/70 ${
                        event.type === "cell" ? "" : "rbc-event-content-header"
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (event.type === "cell") {
                          handleDetails(event);
                        }
                      }}
                    >
                      {event.start.toString().indexOf("00:00:00") === -1
                        ? moment(event.start).format("HH:mm")
                        : event.title}
                    </Button>
                  );
                },
              },
            }}
            formats={{
              eventTimeRangeFormat: () => {
                return "";
              },
              timeGutterFormat: (date, culture, localizer) => {
                const time = localizer.format(date, "HH:mm", culture);
                let rs = "";
                switch (time) {
                  case "00:00":
                    rs = "";
                    break;
                  case "06:00":
                    rs = "";
                    break;
                  case "12:00":
                    rs = "";
                    break;
                  case "18:00":
                    rs = "";
                    break;
                }
                return rs;
              },
            }}
          />
        )}
        {showAs === "table" && (
          <>
            <div className="flex flex-row my-4">
              <div>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handlePrev();
                  }}
                >
                  &lt;
                </SecondaryButton>
                <SecondaryButton
                  className="mr-4"
                  onClick={() => {
                    handleToday();
                  }}
                >
                  Hoy
                </SecondaryButton>
                <SecondaryButton
                  className=""
                  onClick={() => {
                    handleNext();
                  }}
                >
                  &gt;
                </SecondaryButton>
              </div>
            </div>
            <Table
              rowKey={(i) => i.id}
              className="w-full mt-6"
              dataSource={dataTable}
              columns={columns}
              loading={
                loading
                  ? { indicator: antIcon, className: "text-custom_blue" }
                  : false
              }
              locale={{
                emptyText: <Empty description={translate("No hay datos")} />,
                sortTitle: translate("Ordenar"),
                triggerDesc: translate("Ordenar (descendente)"),
                triggerAsc: translate("Ordenar (ascendente)"),
                cancelSort: translate("Cancelar orden"),
                filterTitle: translate("Filtros"),
                filterConfirm: translate("Aceptar"),
                filterReset: translate("Limpiar"),
                filterEmptyText: translate("No hay filtros"),
                filterCheckall: translate("Seleccinar todos"),
                filterSearchPlaceholder: translate("Buscar en filtros"),
              }}
            />
          </>
        )}
        <Modal
          width={600}
          open={modalDetails !== null}
          title={translate("Detalles de la actividad")}
          onCancel={handleHideDetails}
          footer={[
            <Button key="ok" type="primary" onClick={handleHideDetails}>
              {translate("Aceptar")}
            </Button>,
          ]}
        >
          {modalDetails !== null && (
            <Row>
              <Col className="flex-1">
                <Row>
                  <Col>
                    <Text>
                      <Text strong>Fecha:</Text>{" "}
                      {moment(
                        `${modalDetails.activity_date} ${modalDetails.activity_time}`
                      ).format(
                        `${
                          get_language === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY"
                        } HH:mm`
                      )}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>
                      <Text strong>Calorías:</Text> {modalDetails.calories}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>
                      <Text strong>Estado de ánimo:</Text>{" "}
                      {modalDetails.mood ? moods[modalDetails.mood] : "-"}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text>
                      <Text strong>Texto:</Text> {modalDetails.text}
                    </Text>
                  </Col>
                </Row>
                {modalDetails.image_url && (
                  <Row>
                    <Col>
                      <Image
                        src={modalDetails.image_url}
                        className="max-h-80"
                      />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          )}
        </Modal>
      </Col>
    </Row>
  );
};

export default PhysicalActivityCalendar;
